import {
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import React from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { MdDelete } from 'react-icons/md';
import styles from '../styles.module.scss';
import { ETIAnswerV1, ETIFormData } from '../types';
import Answer from './Answer';

export interface QuestionFormProps {
  className?: string;
  control: Control<Omit<ETIFormData, 'taskId' | 'status'>>;
  setValue?: UseFormSetValue<Omit<ETIFormData, 'taskId' | 'status'>>;
  index: number;
  onRemove: () => void;
  isDisabled: boolean;
  isReadOnly: boolean;
  answers: ETIAnswerV1[];
  isVersion2: boolean;
  questionNumber?: number;
  sectionStart?: boolean;
  onSectionBlur?: () => void;
}

const QuestionForm = ({
  control,
  setValue,
  className,
  index,
  onRemove,
  isDisabled,
  isReadOnly,
  answers,
  isVersion2,
  questionNumber,
  onSectionBlur,
}: QuestionFormProps) => {
  const questionType = useWatch({
    control,
    name: `questions.${index}.questionType` as const,
  });

  return (
    <TableRow className={className}>
      <TableCell className={styles.NumberCell} sx={{ paddingTop: '20px' }}>
        {questionNumber}
      </TableCell>
      <TableCell className={styles.QuestionTypeCell}>
        <Controller
          control={control}
          name={`questions.${index}.questionType`}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              variant="standard"
              error={!!fieldState.error?.message}
            >
              <Select
                disabled={isDisabled || isReadOnly}
                displayEmpty
                {...field}
                value={field.value || ''}
                onChange={e => {
                  field.onChange(e);
                  const value = e.target.value as string;
                  // When switching away from radio/checkbox, clear options/optionsRaw and filters
                  if (!(value === 'radio' || value === 'checkbox')) {
                    if (setValue) {
                      setValue(`questions.${index}.options`, undefined as any, {
                        shouldDirty: true,
                      });
                      setValue(`questions.${index}.optionsRaw`, '', {
                        shouldDirty: true,
                      });
                      setValue(`questions.${index}.filter`, undefined as any, {
                        shouldDirty: true,
                      });
                      setValue(`questions.${index}.filterRaw`, '', {
                        shouldDirty: true,
                      });
                    }
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select Type</em>
                </MenuItem>
                <MenuItem value="text">text</MenuItem>
                <MenuItem value="optional_text">optional_text</MenuItem>
                <MenuItem value="radio">radio</MenuItem>
                <MenuItem value="checkbox">checkbox</MenuItem>
                <MenuItem value="attachment">attachment</MenuItem>
              </Select>
              {fieldState.error?.message && (
                <FormHelperText>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </TableCell>
      <TableCell className={styles.SectionCell}>
        <Controller
          control={control}
          name={`questions.${index}.section`}
          render={({ field, fieldState }) => (
            <TextField
              disabled={isDisabled}
              fullWidth
              placeholder="Section"
              error={!!fieldState.error?.message}
              id="section"
              helperText={fieldState.error?.message}
              variant="standard"
              slotProps={{ input: { readOnly: isReadOnly } }}
              {...field}
              onBlur={e => {
                field.onBlur();
                // Trim the value when user loses focus
                if (e.target.value !== e.target.value.trim()) {
                  field.onChange(e.target.value.trim());
                }
                // Trigger numbering recalculation
                onSectionBlur?.();
              }}
            />
          )}
        />
      </TableCell>
      <TableCell className={styles.Cell}>
        <Controller
          control={control}
          name={`questions.${index}.question`}
          render={({ field, fieldState }) => (
            <TextField
              disabled={isDisabled}
              fullWidth
              multiline
              minRows={1}
              placeholder="Question"
              error={!!fieldState.error?.message}
              id="question"
              helperText={fieldState.error?.message}
              variant="standard"
              slotProps={{ input: { readOnly: isReadOnly } }}
              {...field}
              onBlur={e => {
                field.onBlur();
                // Trim the value when user loses focus
                if (e.target.value !== e.target.value.trim()) {
                  field.onChange(e.target.value.trim());
                }
              }}
            />
          )}
        />
      </TableCell>
      <TableCell className={styles.Cell}>
        {questionType === 'radio' || questionType === 'checkbox' ? (
          <Controller
            control={control}
            name={`questions.${index}.optionsRaw`}
            render={({ field, fieldState }) => (
              <TextField
                disabled={isDisabled}
                fullWidth
                multiline
                minRows={1}
                variant="standard"
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
                slotProps={{ input: { readOnly: isReadOnly } }}
                {...field}
              />
            )}
          />
        ) : (
          <div />
        )}
      </TableCell>
      <TableCell className={styles.Cell}>
        <Controller
          control={control}
          name={`questions.${index}.filterRaw`}
          render={({ field, fieldState }) => (
            <TextField
              disabled={isDisabled}
              fullWidth
              multiline
              minRows={1}
              variant="standard"
              error={!!fieldState.error?.message}
              helperText={fieldState.error?.message}
              slotProps={{ input: { readOnly: isReadOnly } }}
              {...field}
            />
          )}
        />
      </TableCell>
      {!isReadOnly ? (
        <TableCell className={styles.ActionsCell} align="center">
          <IconButton disabled={isDisabled} onClick={onRemove}>
            <MdDelete />
          </IconButton>
        </TableCell>
      ) : (
        !isVersion2 && (
          <TableCell className={styles.Cell}>
            <Answer answer={answers?.[index]} />
          </TableCell>
        )
      )}
    </TableRow>
  );
};

export default React.memo(QuestionForm);
